import React, { Fragment, useMemo, useState } from 'react';
import {
    Checkbox,
    CircularProgress,
    Grid2 as Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    styled,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiCog } from '@mdi/js';
import {
    CartesianGrid,
    Legend,
    LineChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { DataKey } from 'recharts/types/util/types';

import { useAppSelector } from '../../state/hooks';
import { ApiStatus, DayOfWeek } from '../../utils/enums';
import { formatNumber } from '../../utils/formattingUtils';

const classesPrefix = 'balanceLineChart';

const classes = {
    loadingWrapper: `${classesPrefix}-loadingWrapper`,
    labelWrapper: `${classesPrefix}-labelWrapper`,
    label: `${classesPrefix}-label`,
    tooltipWrapper: `${classesPrefix}-tooltipWrapper`,
    tooltipLabel: `${classesPrefix}-tooltipLabel`,
    tooltipValue: `${classesPrefix}-tooltipValue`,
    menuPaper: `${classesPrefix}-menuPaper`,
    menuHeader: `${classesPrefix}-menuHeader`,
    menuSecondaryItem: `${classesPrefix}-menuSecondaryItem`,
    inboundLine: `${classesPrefix}-inboundLine`,
    outboundLine: `${classesPrefix}-outboundLine`,
    balanceLine: `${classesPrefix}-balanceLine`,
};

const StyledGrid = styled(Grid)(() => {
    return {
        [`& .${classes.loadingWrapper}`]: {
            minHeight: '300px',
            height: '100%',
            textAlign: 'center',
        },
        [`& .${classes.labelWrapper}`]: {
            display: 'flex',
        },
        [`& .${classes.label}`]: {
            flexGrow: 1,
        },
        [`& .${classes.tooltipWrapper}`]: {
            padding: '8px',
        },
        [`& .${classes.tooltipLabel}`]: {
            fontSize: '16px',
            fontWeight: 600,
        },
        [`& .${classes.tooltipValue}`]: {
            fontSize: '14px',
        },
    };
});

const StyledMenu = styled(Menu)(({ theme }) => {
    return {
        [`& .${classes.menuPaper}`]: {
            width: '280px',
        },
        [`& .${classes.menuHeader}`]: {
            margin: '4px 16px',
        },
        [`& .${classes.menuSecondaryItem}`]: {
            width: '24px',
        },
        [`& .${classes.inboundLine}`]: {
            height: '3px',
            width: '30px',
            backgroundColor: theme.palette.info.light,
        },
        [`& .${classes.outboundLine}`]: {
            height: '3px',
            width: '30px',
            backgroundColor: theme.palette.grey[600],
        },
        [`& .${classes.balanceLine}`]: {
            height: '3px',
            width: '30px',
            background: `repeating-linear-gradient(to right, transparent, transparent 5px, ${theme.palette.secondary.main} 5px, ${theme.palette.secondary.main} 10px)`,
        },
    };
});

const BalanceLineChart = ({
    selectedBalanceView,
    handleBackClick,
}: {
    selectedBalanceView: DayOfWeek | 'daily';
    handleBackClick: () => void;
}): React.JSX.Element => {
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showInboundOrders, setShowInboundOrders] = useState(true);
    const [showOutboundOrders, setShowOutboundOrders] = useState(true);
    const [showBalance, setShowBalance] = useState(true);
    const [opacity, setOpacity] = useState({
        inbound_freight_order_count: 1,
        outbound_freight_order_count: 1,
        balance_order_count: 1,
    });

    const areaBalanceDetailsApiStatus = useAppSelector((state) => { return state.areaBalance.areaBalanceDetailsApiStatus; });
    const areaBalanceDetails = useAppSelector((state) => { return state.areaBalance.areaBalanceDetails; });

    const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = (): void => {
        setAnchorEl(null);
    };

    const handleMouseEnter = (data: Payload & { dataKey?: DataKey<string>; }): void => {
        const { dataKey } = data;

        setOpacity((currentState) => {
            return {
                ...currentState,
                [dataKey as string]: 0.5,
            };
        });
    };

    const handleMouseLeave = (data: Payload & { dataKey?: DataKey<string>; }): void => {
        const { dataKey } = data;

        setOpacity((currentState) => {
            return {
                ...currentState,
                [dataKey as string]: 1,
            };
        });
    };

    const chartData = useMemo(() => {
        if (selectedBalanceView === 'daily') {
            return areaBalanceDetails.daily_balance_chart_data;
        }

        return areaBalanceDetails.hourly_balance_chart_data[selectedBalanceView];
    }, [selectedBalanceView, areaBalanceDetails]);

    const renderChartTitle = (): string => {
        if (selectedBalanceView === 'daily') {
            return 'Balance by Day';
        }

        return `Balance on ${selectedBalanceView}`;
    };

    if (areaBalanceDetailsApiStatus === ApiStatus.Loading) {
        return (
            <StyledGrid container spacing={2}>
                <Grid size={12}>
                    <Typography variant='h4' component='div' className={classes.labelWrapper}>
                        {
                            selectedBalanceView !== 'daily' &&
                            <IconButton disabled size='large'>
                                <Icon path={mdiArrowLeft} size={1} />
                            </IconButton>
                        }
                        <span className={classes.label}>{renderChartTitle()}</span>
                        <IconButton disabled size='large'>
                            <Icon path={mdiCog} size={1} />
                        </IconButton>
                    </Typography>
                </Grid>
                <Grid size={12} className={classes.loadingWrapper}>
                    <CircularProgress />
                </Grid>
            </StyledGrid>
        );
    }

    const renderCustomizedXAxisTick = (chartProps: any): JSX.Element => {
        const {
            x, y, payload,
        } = chartProps;

        return (
            <text
                x={x}
                y={y}
                dy={10}
                textAnchor='middle'
                fill={theme.palette.text.primary}
                fontSize={12}
                fontWeight={400}
            >
                {payload.value}
            </text>
        );
    };

    const renderCustomizedDot = (chartProps: any): JSX.Element => {
        const {
            cx, cy, payload,
        } = chartProps;

        let color: string;
        const absoluteBalance = Math.abs(payload.balance_order_count);

        if (absoluteBalance <= 2) {
            color = theme.palette.success.main;
        } else if (absoluteBalance <= 5) {
            color = theme.palette.warning.main;
        } else {
            color = theme.palette.error.main;
        }

        return (
            <circle
                key={`circle-${cx}${cy}`}
                cx={cx}
                cy={cy}
                r={5}
                fill={theme.palette.common.white}
                stroke={color}
                strokeWidth={4}
            />
        );
    };

    const renderCustomToolTip = ({ active, payload, label }: TooltipProps<number, string>): JSX.Element => {
        if (active && payload && payload.length > 0) {
            return (
                <Paper className={classes.tooltipWrapper}>
                    <Typography className={classes.tooltipLabel} gutterBottom variant='body1'>
                        {label}
                    </Typography>
                    {
                        payload.map((item) => {
                            return (
                                <Typography
                                    key={item.name}
                                    className={classes.tooltipValue}
                                    variant='body2'
                                    style={{
                                        color: item.color,
                                    }}
                                >
                                    <span>{`${item.name}: `}</span>
                                    <span>{formatNumber(item.value)}</span>
                                </Typography>
                            );
                        })
                    }
                </Paper>
            );
        }

        return <Fragment />;
    };

    return (
        <StyledGrid container spacing={2}>
            <Grid size={12}>
                <Typography variant='h4' component='div' className={classes.labelWrapper}>
                    {
                        selectedBalanceView !== 'daily' &&
                        <Tooltip title='Back To Balance by Day' placement='top'>
                            <IconButton
                                size='large'
                                onClick={(): void => {
                                    handleBackClick();
                                }}
                            >
                                <Icon path={mdiArrowLeft} size={1} />
                            </IconButton>
                        </Tooltip>
                    }

                    <span className={classes.label}>{renderChartTitle()}</span>

                    <IconButton
                        aria-controls='settings-menu'
                        aria-haspopup='true'
                        size='large'
                        onClick={handleSettingsClick}
                    >
                        <Icon path={mdiCog} size={1} />
                    </IconButton>
                    <StyledMenu
                        id='settings-menu'
                        classes={{
                            paper: classes.menuPaper,
                        }}
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleSettingsClose}
                    >
                        <Typography variant='h6' className={classes.menuHeader}>Data Display</Typography>
                        <MenuItem
                            onClick={(): void => {
                                setShowInboundOrders((isChecked) => {
                                    return !isChecked;
                                });
                            }}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    edge='start'
                                    checked={showInboundOrders}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': 'checkbox-list-label-inbound' }}
                                />
                            </ListItemIcon>
                            <ListItemText>Show inbound orders</ListItemText>
                            <div className={classes.menuSecondaryItem}>
                                <div className={classes.inboundLine} />
                            </div>
                        </MenuItem>

                        <MenuItem
                            onClick={(): void => {
                                setShowOutboundOrders((isChecked) => {
                                    return !isChecked;
                                });
                            }}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    edge='start'
                                    checked={showOutboundOrders}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': 'checkbox-list-label-outbound' }}
                                />
                            </ListItemIcon>
                            <ListItemText>Show outbound orders</ListItemText>
                            <div className={classes.menuSecondaryItem}>
                                <div className={classes.outboundLine} />
                            </div>
                        </MenuItem>

                        <MenuItem
                            onClick={(): void => {
                                setShowBalance((isChecked) => {
                                    return !isChecked;
                                });
                            }}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    edge='start'
                                    checked={showBalance}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': 'checkbox-list-label-balance' }}
                                />
                            </ListItemIcon>
                            <ListItemText>Show balance</ListItemText>
                            <div className={classes.menuSecondaryItem}>
                                <div className={classes.balanceLine} />
                            </div>
                        </MenuItem>
                    </StyledMenu>
                </Typography>
            </Grid>
            <Grid size={12}>
                <ResponsiveContainer width='100%' height={300}>
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis
                            dataKey='balance_timestamp'
                            domain={['min', 'max']}
                            tick={renderCustomizedXAxisTick}
                        />
                        <YAxis
                            interval='preserveStartEnd'
                            label={{
                                angle: -90,
                                dx: -25,
                                position: 'outsideLeft',
                                fill: theme.palette.text.primary,
                            }}
                        />
                        <ChartTooltip
                            content={renderCustomToolTip}
                        />
                        <Legend
                            iconType='plainline'
                            iconSize={26}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />
                        <ReferenceLine
                            y={0}
                            ifOverflow='extendDomain'
                            stroke={theme.palette.text.primary}
                            strokeWidth={1}
                        />

                        {
                            showInboundOrders &&
                            <Line
                                type='linear'
                                dataKey='inbound_freight_order_count'
                                name='Inbound Orders'
                                strokeOpacity={opacity.inbound_freight_order_count}
                                strokeWidth={2}
                                stroke={theme.palette.info.light}
                            />
                        }

                        {
                            showOutboundOrders &&
                            <Line
                                type='linear'
                                dataKey='outbound_freight_order_count'
                                name='Outbound Orders'
                                strokeOpacity={opacity.outbound_freight_order_count}
                                strokeWidth={2}
                                stroke={theme.palette.grey[600]}
                            />
                        }

                        {
                            showBalance &&
                            <Line
                                type='linear'
                                dataKey='balance_order_count'
                                name='Balance'
                                strokeOpacity={opacity.balance_order_count}
                                strokeWidth={3}
                                stroke={theme.palette.secondary.main}
                                strokeDasharray='5 5'
                                dot={renderCustomizedDot}
                            />
                        }
                    </LineChart>
                </ResponsiveContainer>
            </Grid>
        </StyledGrid>
    );
};

export default BalanceLineChart;
